/**
 * useAgreements.ts — every read the partner's signing and viewing surfaces make.
 *
 * Adapted from the driver app's hook of the same name. What changed, and why:
 *
 *   usePartnerAgreements   the Agreements cards. `get_my_partner_agreements()` is ZERO-ARGUMENT
 *                          and session-scoped — it answers for `my_partner_id()` and for nobody
 *                          else. The driver's `get_agreement_documents(p_contract_id)` has no
 *                          partner equivalent, and could not: a partner holds vehicles across
 *                          many contracts and has no single contract to name.
 *   useAgreementDocument   ONE row, straight from `agreement_documents` under RLS. The policy is
 *                          `can_read_agreement_document(id)`, whose PARTNER_MANAGEMENT arm admits
 *                          the partner the referent STAMPS and nobody else — not the contract's
 *                          driver, and not a partner who acquired the vehicle afterwards.
 *   useAgreementRender     the text, the hash that goes with it, and `path_scope` — the folder the
 *                          ceremony files its signature under, derived by the server rather than
 *                          by this app.
 *   useAgreementMergeValues  THE ONE HOOK WITH NO DRIVER COUNTERPART, and the reason the partner
 *                          app needs no `agreement-key-terms.ts`. See its own note below.
 *   usePendingSignatures   "what is waiting for ME", zero-argument by design: the answer is a
 *                          property of the session, not a filter. The function grew a PARTNER limb
 *                          alongside its DRIVER one, so the shape here is the driver's unchanged.
 *   useSignedCopy          the executed PDF's BYTES, for the in-app viewer. Never a file.
 *
 * NOT PORTED: `useAgreementDocuments` (contract-scoped, replaced above), `useContractInsights`
 * (the driver's key-terms source; this app reads `agreement_merge_data` instead) and
 * `useAgreementArcIndex` (it maps legacy `loans` / `insurance_loans` rows onto in-app documents,
 * and the partner arc has no legacy rows to map).
 *
 * `(supabase as any).rpc(...)` throughout, as this repo has no generated `types.ts` at all.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { isTransportFailure } from '@/lib/signed-copy-failure';

// ─── Shapes ──────────────────────────────────────────────────────────────────

export interface AgreementSignatureSummary {
    party: string;
    role_label: string | null;
    sign_order: number | null;
    status: string | null;
    signer_name: string | null;
    signed_at: string | null;
}

/**
 * One row of `get_my_partner_agreements()`, column for column as its `RETURNS TABLE` declares
 * them.
 *
 * ── WHAT IT DOES NOT CARRY, AND WHAT TO DO INSTEAD ────────────────────────────
 * `doc_type` — every row is a PARTNER_MANAGEMENT document by construction, so the function does
 * not spend a column restating it. A caller wanting `documentLabel(title, docType)` passes the
 * constant.
 *
 * `signatures[]` — the driver's list RPC returns the whole roster; this one returns
 * `signature_status`, THE CALLER'S OWN SEAT, deliberately: the company's countersignature is a
 * fact about the document's `status`, which is the column beside it, and what a partner needs to
 * know is whether THEY have signed. A card that wants the full roster reads `parties` off
 * `useAgreementRender`.
 */
export interface PartnerAgreementSummary {
    document_id: number;
    contract_id: number;
    vehicle_id: number;
    /** "Toyota Corolla GT-1234 24", or null where the vehicle resolves no words at all. */
    vehicle_label: string | null;
    reference_no: string;
    title: string;
    /** The DOCUMENT's status: PENDING_REVIEW, IN_SIGNING, COMPLETED, VOIDED, SUPERSEDED. */
    status: string;
    /** The PARTNER seat's own status: PENDING, SENT, VIEWED, SIGNED, DECLINED, VOIDED. */
    signature_status: string | null;
    created_at: string;
    finalized_at: string | null;
    /**
     * The booklet the Schedule incorporates, resolved through the template it is pinned to.
     *
     * A HINT AND NEVER THE ANSWER, exactly as in the driver app: the booklet an instrument
     * incorporates is the one its frozen clause 3.1 cites, under the signer's own hash. A foreign
     * key is a row that can be repointed. See `agreement-terms-authority.ts`.
     */
    terms_id: number | null;
    terms_form_code: string | null;
    terms_version: number | null;
    terms_sha256: string | null;
    /** From `agreement_path_scope` — the same derivation `agreement_render` hands back. */
    path_scope: string | null;
    /**
     * The two figures off the referent, frozen at seal, so a row and the page it opens agree.
     * For a schedule generated from [D-B59] on, `all_in_cost` holds the contract's Cash price,
     * copied onto the referent at generation; an earlier row holds the figure the partner
     * stated under [D-B48]. Both print under the label "Cash price", and the column keeps its
     * historical name.
     */
    all_in_cost: number | null;
    management_rate: number | null;
}

/**
 * The columns the ceremony needs from the row ITSELF.
 *
 * `title` and `completed_at` are NOT among them: both were dropped as columns by
 * `20260801060000_agreement_schema_minimization_and_deposit_links.sql`, and PostgREST answers a
 * select naming a column that is gone with a 42703 rather than with a null — so asking for either
 * would break the ceremony outright. The title comes from `agreement_render` (pinned template).
 *
 * `contract_partner_agreement_id` is this arc's subject, added by
 * `20260908010000_the_partner_gets_a_referent_a_seat_and_an_address.sql:501`. The driver's three
 * arc columns are not read here: a PARTNER_MANAGEMENT document carries none of them — the
 * `agreement_documents_exactly_one_subject` CHECK sees to that.
 *
 * `contract_id` is typed nullable because the COLUMN is, across the whole table. On this arc it is
 * always stamped, and nothing here depends on that being so.
 */
export interface AgreementDocumentRow {
    id: number;
    contract_id: number | null;
    doc_type: string;
    status: string;
    reference_no: string;
    created_at: string;
    frozen_at: string | null;
    finalized_at: string | null;
    contract_partner_agreement_id: number | null;
}

const DOCUMENT_COLUMNS =
    'id, contract_id, doc_type, status, reference_no, created_at, frozen_at, ' +
    'finalized_at, contract_partner_agreement_id';

/**
 * `agreement_render`'s payload as a PARTNER sees it — the admin-only keys stay null.
 *
 * `values`, `missing`, `drifted`, `final_bucket` and `final_path` are masked to everyone but an
 * admin (`20260908010000_the_partner_gets_a_referent_a_seat_and_an_address.sql:2818-2822`),
 * which is precisely why `useAgreementMergeValues` exists.
 */
export interface AgreementRenderPayload {
    document_id: number;
    doc_type: string;
    reference_no: string;
    title: string;
    status: string;
    frozen: boolean;
    frozen_at: string | null;
    rendered_md: string;
    content_sha256: string;
    parties: AgreementSignatureSummary[] | null;
    missing: string[] | null;
    values: Record<string, unknown> | null;
    drifted: boolean | null;
    final_bucket: string | null;
    final_path: string | null;
    /**
     * WHERE THIS DOCUMENT'S OBJECTS ARE FILED, derived server-side by `agreement_path_scope`.
     *
     * NOT admin-masked, unlike the five keys above it: those are diagnostics about a render, this
     * is the ADDRESS the ceremony composes its own signature path from. It is the one value on
     * this payload the signing step cannot proceed without.
     */
    path_scope: string | null;
}

/**
 * `agreement_merge_data`'s payload — the resolved values map, the template's required keys, and
 * the pre-flight list of the ones that came back empty.
 */
export interface AgreementMergePayload {
    document_id: number;
    doc_type: string;
    values: Record<string, unknown> | null;
    required: string[] | null;
    missing: string[] | null;
}

/** One row of `get_my_pending_signatures()`. Unchanged from the driver app's shape. */
export interface PendingSignature {
    document_id: number;
    contract_id: number;
    doc_type: string;
    reference_no: string;
    title: string;
    party: string;
    role_label: string | null;
    sign_order: number | null;
    signature_status: string;
    document_status: string;
    created_at: string;
    /** The same server-derived scope, so the banner's target needs no second read. */
    path_scope: string | null;
}

// ─── Absence is not an error ─────────────────────────────────────────────────

/**
 * True when the failure is "this subsystem is not on this database yet".
 *
 * The app and the migration land independently. An Agreements page that threw (and reported to
 * Sentry, for every partner, on every visit) until the migration applied would make an expected
 * sequencing gap look like an outage.
 *
 * PGRST202 is PostgREST's "no such function", PGRST205 its "no such table". Anything else — a
 * denied read, a broken argument, a real fault — still throws.
 */
function isSubsystemAbsent(error: unknown): boolean {
    const code = (error as { code?: string } | null)?.code;
    return code === 'PGRST202' || code === 'PGRST205';
}

// ─── Queries ─────────────────────────────────────────────────────────────────

/**
 * Every Vehicle Schedule the CALLING partner is a party to, newest first.
 *
 * NO ARGUMENT, AND THAT IS THE DESIGN. The function keys on `my_partner_id()` and returns an
 * EMPTY SET — not a refusal — for a session that is not a partner, so there is no id for this app
 * to hold, pass, or get wrong.
 *
 * It keys on the STAMPED referent partner, so a vehicle that changes hands leaves the executed
 * schedule with the partner who executed it, and does not hand it to the new owner.
 */
export function usePartnerAgreements(enabled = true) {
    return useQuery({
        queryKey: ['partner-agreements'],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_my_partner_agreements');
            if (error) {
                if (isSubsystemAbsent(error)) return [] as PartnerAgreementSummary[];
                throw error;
            }
            return (data ?? []) as PartnerAgreementSummary[];
        },
        enabled,
    });
}

export function useAgreementDocument(documentId: number | null | undefined) {
    return useQuery({
        queryKey: ['agreement-document', documentId],
        queryFn: async () => {
            const { data, error } = await (supabase as any)
                .from('agreement_documents')
                .select(DOCUMENT_COLUMNS)
                .eq('id', documentId)
                .maybeSingle();
            if (error) throw error;
            return (data ?? null) as AgreementDocumentRow | null;
        },
        enabled: !!documentId,
    });
}

export function useAgreementRender(documentId: number | null | undefined) {
    return useQuery({
        queryKey: ['agreement-render', documentId],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('agreement_render', {
                p_document_id: documentId,
            });
            if (error) throw error;
            return (data ?? null) as AgreementRenderPayload | null;
        },
        enabled: !!documentId,
        // The frozen text never changes; a stale render is only ever a stale party roster, and the
        // ceremony re-reads the hash from the server on every signing attempt.
        staleTime: 30_000,
    });
}

/**
 * The resolved merge values — the figures the confirmation step states back to the partner.
 *
 * ── WHY THIS EXISTS, AND WHY THE DRIVER APP HAS NO SUCH HOOK ──────────────────
 * `agreement_render` masks `values` to everyone but an admin, so the driver app re-derives its
 * plain-English key terms from `get_contract_insights` in a 644-line module. The partner does not
 * have that problem: `agreement_merge_data` is its own granted entry point (`authenticated`) and
 * BOTH its gates fork for PARTNER_MANAGEMENT to admit the STAMPED referent partner — the contract
 * gate at `20260908010000_the_partner_gets_a_referent_a_seat_and_an_address.sql:3048-3059` and the
 * content gate at `:3084-3090`. So the seven money figures and the particulars beside them are
 * read straight, server-formatted, and this app re-solves NONE of them — the page and the
 * instrument are made of the same arithmetic, rounded once, in SQL.
 *
 * ── IT RESOLVES AT CALL TIME, AND THE SERVER IS WHAT KEEPS THAT SAFE ──────────
 * The body never touches `frozen_merge_data`. Half of what it hands back is nonetheless already
 * frozen: the partner's particulars and the two figures every other one divides by —
 * `all_in_cost` (the contract's Cash price, copied onto the referent at generation from
 * [D-B59] on; the partner's own stated figure on an earlier row) and `management_rate` — come
 * off the REFERENT and not off the live world
 * (`20260908010000_the_partner_gets_a_referent_a_seat_and_an_address.sql:3227-3248`), and
 * `contract_partner_agreement_derive_facts` (`:403`) stops that row moving at the PARTNER's
 * signature rather than at the seal. The other half — the Hire-purchase price, the weekly rate,
 * the term — resolves off the contract as it stands now.
 *
 * So a frozen page and these values could in principle disagree, and NOTHING HERE RECONCILES
 * THEM — nor should it. `trg_contracts_void_partner_schedule` watches the four columns that
 * would cause it, `vehicle_id`, `management_rate`, `sold_at` (the selling price) and
 * `cash_price` [D-B59], and ONLY WHILE THE CONTRACT IS DRAFT: there a change to any of them
 * deletes a schedule not yet executed together with its referent, so it is raised again on the
 * corrected figures, and an executed schedule on a DRAFT contract makes it refuse the change.
 * Past DRAFT the trigger does nothing at all, so a Cash price corrected on a live contract
 * leaves an unsent schedule stating its copy until an admin withdraws and regenerates it. A
 * client-side comparison would be a second opinion with no authority, and this app cannot form
 * one anyway — `agreement_render` masks `drifted` to admins.
 *
 * Its `values` are therefore cached exactly as long as the render they accompany.
 */
export function useAgreementMergeValues(documentId: number | null | undefined) {
    return useQuery({
        queryKey: ['agreement-merge-data', documentId],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('agreement_merge_data', {
                p_document_id: documentId,
            });
            if (error) throw error;
            return (data ?? null) as AgreementMergePayload | null;
        },
        enabled: !!documentId,
        staleTime: 30_000,
    });
}

export function usePendingSignatures(enabled = true) {
    return useQuery({
        queryKey: ['my-pending-signatures'],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_my_pending_signatures');
            if (error) {
                if (isSubsystemAbsent(error)) return [] as PendingSignature[];
                throw error;
            }
            return (data ?? []) as PendingSignature[];
        },
        enabled,
    });
}

// ─── The executed copy ───────────────────────────────────────────────────────

/**
 * The executed PDF's BYTES, for the in-app viewer.
 *
 * THE FILE-MAKING HALF IS ABSENT, and that is the ruling [D-B36]: a signed instrument is SHOWN
 * and not handed over. No object URL, no anchor with a `download` attribute, no navigation to the
 * bytes, nothing a browser would treat as a download. What is left is an `ArrayBuffer` the sealed
 * viewer paints onto a canvas.
 *
 * It is a QUERY rather than a mutation because it is a read the page performs to draw itself, not
 * an action a partner takes: the viewer needs the bytes to render at all, and a mutation's "has it
 * been fired yet" is the wrong state for that.
 *
 * The function runs the caller's own JWT, so who may read a document is decided in SQL and not
 * here. `functions.invoke` hands back a Blob for an `application/pdf` response, which is why this
 * is not a `fetch` of a signed URL: the archived object lives in a bucket a partner can neither
 * list nor mint an address in.
 *
 * ── ONE RETRY, AND ONLY FOR THE FAILURE A RETRY CAN MOVE ──────────────────────
 * `my_signed_copy` is the sole route to these bytes, so a cold edge instance is the difference
 * between a partner seeing their agreement and seeing an error. A request that never reached a
 * verdict is retried once. A verdict — not finalized, not yours, refused — is an answer, and
 * asking again just spends another round trip on the same no.
 */
export function useSignedCopy(
    documentId: number | null | undefined,
    options: { enabled?: boolean } = {},
) {
    return useQuery({
        queryKey: ['agreement-signed-copy', documentId],
        queryFn: async (): Promise<ArrayBuffer> => {
            const { data, error } = await supabase.functions.invoke('generate-agreement', {
                body: { action: 'my_signed_copy', document_id: documentId },
            });
            if (error) throw error;
            const blob = data as Blob | null;
            if (!(blob instanceof Blob)) {
                throw new Error('AGREEMENT_COPY_NOT_A_PDF: the copy did not arrive as a document');
            }
            return await blob.arrayBuffer();
        },
        enabled: !!documentId && options.enabled !== false,
        // An executed PDF is immutable — it is the sealed instrument — so the bytes are worth
        // exactly one fetch per session. `agreement_finalize` writes it once and never again.
        staleTime: Infinity,
        retry: (failureCount, error) => failureCount < 1 && isTransportFailure(error),
    });
}
